import React, { useEffect, useRef, useState } from "react";
import api from "../api";
import { useAuth } from "../AuthContext";
import AddressAutocomplete from "../components/AddressAutocomplete";
import { browserGeocode } from "../lib/browserGeocode";
import { toast } from "sonner";
import { ConfigurableKpi, useKpiConfig } from "../components/dashboard/KpiLibrary";
import ActualDisposalPanel from "../components/dashboard/ActualDisposalPanel";
import {
  Users, MapTrifold, CurrencyDollar, Recycle, ChartLine, Stack, Truck, Coins,
  CheckCircle, Warning, Clock, ArrowsClockwise, Scales, Receipt, Money, Sliders,
  MapPin, FloppyDisk, Flag, FlagCheckered, DownloadSimple, X, CaretLeft, CaretRight,
} from "@phosphor-icons/react";

const DAYS = ["Mon", "Tue", "Wed", "Thu", "Fri", "Sat", "Sun"];
const fmt$ = (n) => n == null || isNaN(n) ? "—" : `$${Number(n).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
const fmtNum = (n, d = 1) => n == null || isNaN(n) ? "—" : Number(n).toLocaleString(undefined, { minimumFractionDigits: 0, maximumFractionDigits: d });

function Kpi({ label, value, sub, Icon, accent, positive, onClick, testid }) {
  const Tag = onClick ? "button" : "div";
  return (
    <Tag
      onClick={onClick}
      data-testid={testid || `kpi-${label.toLowerCase().replace(/\s+/g, "-")}`}
      className={`bg-white border border-[#012340]/10 rounded-sm p-5 w-full text-left ${onClick ? "hover:border-[#CFAB7A] hover:shadow-sm transition-all cursor-pointer" : ""}`}
    >
      <div className="flex items-start justify-between">
        <div className="text-[11px] font-bold uppercase tracking-[0.25em] text-[#5C738A]">{label}</div>
        <div className={`w-8 h-8 rounded-sm grid place-items-center ${accent ? "bg-[#CFAB7A] text-[#012340]" : "bg-[#012340] text-[#CFAB7A]"}`}>
          <Icon size={16} weight="bold" />
        </div>
      </div>
      <div className={`kpi-number text-3xl mt-3 ${positive === false ? "text-[#D32F2F]" : positive === true ? "text-[#2E7D32]" : "text-[#012340]"}`}>{value}</div>
      {sub && <div className="text-xs text-[#5C738A] mt-1">{sub}</div>}
    </Tag>
  );
}

const DAYS_WEEK = ["Mon", "Tue", "Wed", "Thu", "Fri", "Sat", "Sun"];

function fmtHoursDash(h) {
  if (h == null || isNaN(h)) return "0h 00m";
  const total = Number(h);
  if (total < 1 / 60) return `${Math.max(0, Math.round(total * 3600))}s`;
  const hh = Math.floor(total);
  const mm = Math.round((total - hh) * 60);
  return `${hh}h ${String(mm).padStart(2, "0")}m`;
}

function periodLabelDash(period, day) {
  if (period === "monthly") return "Monthly";
  if (period === "weekly") return "Weekly · All week";
  return `Daily · ${day || "—"}`;
}

function fmtDateIsoDash(iso) {
  if (!iso) return "—";
  try {
    const [y, m, d] = iso.split("-").map(Number);
    return new Date(Date.UTC(y, m - 1, d)).toLocaleDateString(undefined, { month: "short", day: "numeric", year: "numeric", timeZone: "UTC" });
  } catch { return iso; }
}

function CompanyAnalyticsPanel() {
  const [period, setPeriod] = useState("monthly"); // monthly | weekly | daily
  const [day, setDay] = useState(null);            // Mon..Sun (when period=daily)
  const [routes, setRoutes] = useState([]);
  const [selectedRouteIds, setSelectedRouteIds] = useState([]); // [] = all routes
  const [analytics, setAnalytics] = useState(null);
  const [loading, setLoading] = useState(false);
  const [showRouteFilter, setShowRouteFilter] = useState(false);
  const [breakdownOpen, setBreakdownOpen] = useState(false);
  // Per-admin KPI configuration (loaded once from /me/kpi-config, persisted to localStorage + server)
  const [kpiConfig, setKpiConfig] = useKpiConfig(api);

  async function loadRoutes() {
    try {
      const { data } = await api.get("/routes");
      const sorted = [...data].sort((a, b) =>
        String(a.route_number || "").localeCompare(String(b.route_number || ""), undefined, { numeric: true })
      );
      setRoutes(sorted);
    } catch (err) {
      console.warn("Failed to load routes for filter", err);
    }
  }

  async function loadAnalytics() {
    setLoading(true);
    try {
      const params = { period };
      if (period === "daily" && day) params.day = day;
      if (selectedRouteIds.length > 0) params.route_ids = selectedRouteIds.join(",");
      const { data } = await api.get("/analytics/dashboard", { params });
      setAnalytics(data);
    } finally {
      setLoading(false);
    }
  }

  useEffect(() => { loadRoutes(); }, []);
  useEffect(() => { loadAnalytics(); /* eslint-disable-next-line */ }, [period, day, selectedRouteIds.join(",")]);

  function selectPeriod(next) {
    if (next === "monthly") { setPeriod("monthly"); setDay(null); }
    else if (next === "weekly") { setPeriod("weekly"); setDay(null); }
    else { setPeriod("daily"); setDay(next); }
  }

  function toggleRoute(rid) {
    setSelectedRouteIds((cur) => cur.includes(rid) ? cur.filter((x) => x !== rid) : [...cur, rid]);
  }

  function clearRouteFilter() {
    setSelectedRouteIds([]);
  }

  const m = analytics?.metrics || analytics || {};
  const scopeLabel = selectedRouteIds.length === 0
    ? `All routes (${routes.length})`
    : selectedRouteIds.length === 1
      ? `1 route — #${routes.find((r) => r.id === selectedRouteIds[0])?.route_number || ""}`
      : `${selectedRouteIds.length} routes selected`;

  function displayRange() {
    if (!analytics?.period_start || !analytics?.period_end) return "—";
    if (analytics.period === "daily" && analytics.day) {
      try {
        const [y, mo] = analytics.period_start.split("-").map(Number);
        const monthLabel = new Date(Date.UTC(y, mo - 1, 1)).toLocaleDateString(undefined, { month: "long", year: "numeric", timeZone: "UTC" });
        return `Every ${analytics.day} in ${monthLabel}`;
      } catch { /* fall through */ }
    }
    return `${fmtDateIsoDash(analytics.period_start)} → ${fmtDateIsoDash(analytics.period_end)}`;
  }

  const activeIsoTab = period === "monthly" ? "monthly" : period === "weekly" ? "weekly" : day;

  return (
    <section className="space-y-4" data-testid="company-analytics-panel">
      {/* Header strip */}
      <div className="bg-[#012340] text-white border-l-4 border-[#CFAB7A] rounded-sm px-4 py-3 flex flex-wrap items-center justify-between gap-2" data-testid="company-analytics-header">
        <div>
          <div className="text-[10px] font-bold uppercase tracking-[0.3em] text-[#CFAB7A]">Company Performance · {periodLabelDash(period, day)}</div>
          <div className="text-sm font-bold mt-0.5 tabular-nums" data-testid="company-analytics-range">{displayRange()}</div>
        </div>
        <div className="text-[10px] uppercase tracking-[0.25em] text-white/70">{loading ? "Loading…" : `${m.total_customers || 0} customers · ${m.total_containers || 0} containers`}</div>
      </div>

      {/* Filter row */}
      <div className="bg-white border border-[#012340]/10 rounded-sm p-3 flex flex-wrap items-center gap-2">
        <div className="text-[11px] font-bold uppercase tracking-[0.25em] text-[#5C738A] mr-1">Period</div>
        <button onClick={() => selectPeriod("monthly")} data-testid="company-period-monthly" className={`px-3 py-1.5 rounded-sm text-xs font-bold ${activeIsoTab === "monthly" ? "bg-[#012340] text-white" : "bg-white border border-[#012340]/15 text-[#012340] hover:bg-[#012340]/5"}`}>Monthly</button>
        <button onClick={() => selectPeriod("weekly")} data-testid="company-period-week" className={`px-3 py-1.5 rounded-sm text-xs font-bold ${activeIsoTab === "weekly" ? "bg-[#012340] text-white" : "bg-white border border-[#012340]/15 text-[#012340] hover:bg-[#012340]/5"}`}>All week</button>
        {DAYS_WEEK.map((d) => (
          <button key={d} onClick={() => selectPeriod(d)} data-testid={`company-period-${d}`} className={`px-3 py-1.5 rounded-sm text-xs font-bold ${activeIsoTab === d ? "bg-[#012340] text-white" : "bg-white border border-[#012340]/15 text-[#012340] hover:bg-[#012340]/5"}`}>{d}</button>
        ))}
        <div className="ml-auto relative">
          <button
            onClick={() => setShowRouteFilter((s) => !s)}
            data-testid="company-route-filter-toggle"
            className="inline-flex items-center gap-2 px-3 py-1.5 rounded-sm text-xs font-bold bg-white border border-[#012340]/15 text-[#012340] hover:bg-[#012340]/5"
          >
            <MapTrifold size={14} weight="bold" /> {scopeLabel}
          </button>
          {showRouteFilter && (
            <div className="absolute right-0 top-full mt-1 z-30 bg-white border border-[#012340]/15 rounded-sm shadow-md w-72 max-h-72 overflow-y-auto" data-testid="company-route-filter">
              <div className="sticky top-0 bg-white border-b border-[#012340]/10 px-3 py-2 flex items-center justify-between">
                <span className="text-[10px] font-bold uppercase tracking-[0.25em] text-[#5C738A]">Routes</span>
                <button onClick={clearRouteFilter} className="text-[10px] text-[#012340] font-bold hover:underline" data-testid="company-route-filter-all">All</button>
              </div>
              {routes.map((r) => {
                const selected = selectedRouteIds.includes(r.id);
                return (
                  <label key={r.id} className={`flex items-center gap-2 px-3 py-2 text-xs cursor-pointer ${selected ? "bg-[#CFAB7A]/15" : "hover:bg-[#FAFAFA]"}`} data-testid={`company-route-opt-${r.id}`}>
                    <input type="checkbox" checked={selected} onChange={() => toggleRoute(r.id)} />
                    <span className="font-heading font-bold text-[#012340]">#{r.route_number}</span>
                    <span className="text-[#5C738A] truncate">{r.name || "—"}</span>
                  </label>
                );
              })}
              {!routes.length && <div className="px-3 py-3 text-xs text-[#5C738A]">No routes yet.</div>}
            </div>
          )}
        </div>
      </div>

      {/* Card grid — money (each slot is interchangeable via the gear icon).
          slot_3 defaults to Actual Disposal so admins immediately see the
          ticket-driven total sitting alongside Est. Disposal for easy compare. */}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-3">
        <ConfigurableKpi slotId="slot_0" defaultKey="revenue"              metrics={m} analytics={analytics} kpiConfig={kpiConfig} onChange={setKpiConfig} onClick={() => setBreakdownOpen(true)} />
        <ConfigurableKpi slotId="slot_1" defaultKey="profit"               metrics={m} analytics={analytics} kpiConfig={kpiConfig} onChange={setKpiConfig} />
        <ConfigurableKpi slotId="slot_2" defaultKey="disposal_cost"        metrics={m} analytics={analytics} kpiConfig={kpiConfig} onChange={setKpiConfig} />
        <ConfigurableKpi slotId="slot_3" defaultKey="actual_disposal_cost" metrics={m} analytics={analytics} kpiConfig={kpiConfig} onChange={setKpiConfig} />
      </div>

      {/* Revenue per yard now lives in the tonnage row so it's always visible
          without competing with the Est. vs Actual Disposal pair above. */}
      <div className="grid grid-cols-2 lg:grid-cols-5 gap-3">
        <ConfigurableKpi slotId="slot_r_yd" defaultKey="revenue_per_yard"  metrics={m} analytics={analytics} kpiConfig={kpiConfig} onChange={setKpiConfig} />
        <ConfigurableKpi slotId="slot_4" defaultKey="estimated_tonnage"   metrics={m} analytics={analytics} kpiConfig={kpiConfig} onChange={setKpiConfig} />
        <ConfigurableKpi slotId="slot_5" defaultKey="yards_serviced"      metrics={m} analytics={analytics} kpiConfig={kpiConfig} onChange={setKpiConfig} />
        <ConfigurableKpi slotId="slot_6" defaultKey="avg_yards_per_stop"  metrics={m} analytics={analytics} kpiConfig={kpiConfig} onChange={setKpiConfig} />
        <ConfigurableKpi slotId="slot_7" defaultKey="avg_revenue_per_stop" metrics={m} analytics={analytics} kpiConfig={kpiConfig} onChange={setKpiConfig} />
      </div>

      {/* Counts + hours */}
      <div className="grid grid-cols-2 lg:grid-cols-5 gap-3">
        <ConfigurableKpi slotId="slot_8"  defaultKey="stops"             metrics={m} analytics={analytics} kpiConfig={kpiConfig} onChange={setKpiConfig} />
        <ConfigurableKpi slotId="slot_9"  defaultKey="customers"         metrics={m} analytics={analytics} kpiConfig={kpiConfig} onChange={setKpiConfig} />
        <ConfigurableKpi slotId="slot_10" defaultKey="containers"        metrics={m} analytics={analytics} kpiConfig={kpiConfig} onChange={setKpiConfig} />
        <ConfigurableKpi slotId="slot_11" defaultKey="route_hours"       metrics={m} analytics={analytics} kpiConfig={kpiConfig} onChange={setKpiConfig} />
        <ConfigurableKpi slotId="slot_12" defaultKey="disposal_per_ton"  metrics={m} analytics={analytics} kpiConfig={kpiConfig} onChange={setKpiConfig} />
      </div>

      {breakdownOpen && (
        <RevenueBreakdownModal
          period={period}
          day={day}
          routeIds={selectedRouteIds}
          onClose={() => setBreakdownOpen(false)}
        />
      )}
    </section>
  );
}

function RevenueBreakdownModal({ period, day, routeIds, onClose }) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [tab, setTab] = useState("included"); // included | excluded

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const params = { period };
        if (period === "daily" && day) params.day = day;
        if (routeIds.length > 0) params.route_ids = routeIds.join(",");
        const r = await api.get("/analytics/revenue-breakdown", { params });
        if (!cancelled) setData(r.data);
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, [period, day, routeIds.join(",")]); // eslint-disable-line

  // Esc closes
  useEffect(() => {
    const onKey = (e) => { if (e.key === "Escape") onClose(); };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [onClose]);

  const items = (tab === "included" ? data?.included : data?.excluded) || [];

  return (
    <div className="fixed inset-0 z-50 bg-black/60 grid place-items-center p-4" onClick={onClose} data-testid="revenue-breakdown-modal">
      <div onClick={(e) => e.stopPropagation()} className="bg-white w-full max-w-5xl max-h-[90vh] rounded-sm border border-[#012340]/10 overflow-hidden flex flex-col">
        <header className="bg-[#012340] text-white px-5 py-4 flex items-center justify-between">
          <div>
            <div className="text-[10px] font-bold uppercase tracking-[0.3em] text-[#CFAB7A]">Revenue Breakdown</div>
            <div className="font-heading font-bold mt-0.5">
              {data ? fmt$(data.total_revenue) : "…"} · {periodLabelDash(period, day)}
            </div>
          </div>
          <button onClick={onClose} className="p-2 rounded-sm bg-white/10 hover:bg-white/20" data-testid="revenue-breakdown-close">✕</button>
        </header>
        <div className="px-5 py-3 border-b border-[#012340]/10 flex items-center gap-2 flex-wrap">
          <button onClick={() => setTab("included")} data-testid="revenue-tab-included" className={`px-3 py-1.5 rounded-sm text-xs font-bold ${tab === "included" ? "bg-[#012340] text-white" : "bg-white border border-[#012340]/15 text-[#012340]"}`}>
            Included ({data?.included_count ?? "…"})
          </button>
          <button onClick={() => setTab("excluded")} data-testid="revenue-tab-excluded" className={`px-3 py-1.5 rounded-sm text-xs font-bold ${tab === "excluded" ? "bg-[#012340] text-white" : "bg-white border border-[#012340]/15 text-[#012340]"}`}>
            Excluded ({data?.excluded_count ?? "…"})
          </button>
          <div className="ml-auto text-[11px] text-[#5C738A]">
            Same formula as the dashboard + route profile. If a row's revenue surprises you, the <span className="font-bold">Reason</span> column explains why.
          </div>
        </div>
        <div className="flex-1 overflow-auto">
          {loading && <div className="p-6 text-center text-sm text-[#5C738A]">Loading…</div>}
          {!loading && items.length === 0 && (
            <div className="p-6 text-center text-sm text-[#5C738A]" data-testid="revenue-breakdown-empty">
              No {tab} entries.
            </div>
          )}
          {!loading && items.length > 0 && (
            <table className="w-full text-sm">
              <thead className="text-[10px] uppercase tracking-[0.2em] text-[#5C738A] bg-[#FAFAFA] sticky top-0">
                <tr className="text-left">
                  <th className="px-4 py-2">Customer</th>
                  <th className="px-4 py-2">Container</th>
                  <th className="px-4 py-2">Route</th>
                  <th className="px-4 py-2">Schedule</th>
                  <th className="px-4 py-2">Source</th>
                  <th className="px-4 py-2 text-right">Monthly Rate</th>
                  <th className="px-4 py-2 text-right">Revenue</th>
                  <th className="px-4 py-2">{tab === "included" ? "Reason Included" : "Reason Excluded"}</th>
                </tr>
              </thead>
              <tbody>
                {items.map((row) => (
                  <tr key={row.container_id || `${row.customer_id}-${row.container_size}`} className="border-t border-[#012340]/5 even:bg-[#FAFAFA]" data-testid={`revenue-row-${row.container_id}`}>
                    <td className="px-4 py-2 text-[#012340] font-semibold text-xs">{row.customer_name || "—"}</td>
                    <td className="px-4 py-2 text-xs text-[#012340]">{row.waste_stream} · {row.container_size}yd³ × {row.quantity}</td>
                    <td className="px-4 py-2 text-xs">{row.route_label}</td>
                    <td className="px-4 py-2 text-[10px] text-[#5C738A] font-mono">{(row.service_days || []).join(",") || "—"}</td>
                    <td className="px-4 py-2 text-[10px] text-[#5C738A] uppercase tracking-wider font-bold">{row.revenue_source_type}</td>
                    <td className="px-4 py-2 text-xs text-[#012340] font-mono text-right">{fmt$(row.monthly_price)}</td>
                    <td className="px-4 py-2 text-xs font-bold text-[#012340] font-mono text-right">{fmt$(row.revenue)}</td>
                    <td className="px-4 py-2 text-[11px] text-[#5C738A] italic">{row.reason_included || row.reason_excluded || "—"}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>
      </div>
    </div>
  );
}

export default function Dashboard() {
  const { user } = useAuth();
  const isVisitor = user?.role === "visitor";
  const [data, setData] = useState(null);
  const [routes, setRoutes] = useState([]);
  const [dispatch, setDispatch] = useState(null);
  const [previewDay, setPreviewDay] = useState("");
  const [loadingDispatch, setLoadingDispatch] = useState(false);

  // Editable disposal settings on dashboard
  const [tipping, setTipping] = useState("");
  const [ppy, setPpy] = useState("");
  const saveTimer = useRef(null);

  const [showReset, setShowReset] = useState(false);
  const [resetText, setResetText] = useState("");

  async function loadAll() {
    const [a, r] = await Promise.all([
      api.get("/analytics/dashboard"),
      api.get("/routes"),
    ]);
    setData(a.data);
    setRoutes(r.data);
    if (tipping === "" && ppy === "") {
      setTipping(String(a.data.tipping_rate ?? 54.20));
      setPpy(String(a.data.pounds_per_yard ?? 100));
    }
  }
  async function loadDispatch() {
    setLoadingDispatch(true);
    try {
      const params = previewDay ? { day: previewDay } : {};
      const { data } = await api.get("/dispatch/today", { params });
      setDispatch(data);
    } finally {
      setLoadingDispatch(false);
    }
  }

  useEffect(() => { loadAll(); }, []);
  useEffect(() => { loadDispatch(); /* eslint-disable-next-line */ }, [previewDay]);
  useEffect(() => {
    const t = setInterval(() => { loadDispatch(); }, 30000);
    return () => clearInterval(t);
    // eslint-disable-next-line
  }, [previewDay]);

  // Debounced save of settings — and instant analytics refresh
  function scheduleSave(nextTipping, nextPpy) {
    if (saveTimer.current) clearTimeout(saveTimer.current);
    saveTimer.current = setTimeout(async () => {
      try {
        // Preserve other settings — fetch current then patch the two fields
        const { data: current } = await api.get("/settings");
        const payload = {
          ...current,
          tipping_rate: Number(nextTipping) || 0,
          pounds_per_yard: Number(nextPpy) || 0,
        };
        // Strip read-only / non-input keys to avoid validator complaints
        delete payload.id;
        delete payload.demo_cleaned;
        delete payload.demo_cleaned_at;
        delete payload.demo_cleaned_summary;
        delete payload.prod_defaults_applied;
        await api.put("/settings", payload);
        await loadAll();
        toast.success("Disposal settings saved");
      } catch (e) {
        toast.error("Failed to save settings");
      }
    }, 500);
  }

  function onTippingChange(v) {
    setTipping(v);
    scheduleSave(v, ppy);
  }
  function onPpyChange(v) {
    setPpy(v);
    scheduleSave(tipping, v);
  }

  async function doReset() {
    if (resetText.trim().toUpperCase() !== "RESET") {
      toast.error('Type "RESET" to confirm');
      return;
    }
    try {
      const { data } = await api.post("/admin/wipe-data");
      const w = data.wiped || {};
      toast.success(`Wiped: ${w.customers || 0} customers · ${w.routes || 0} routes · ${w.route_reports || 0} reports`);
      setShowReset(false);
      setResetText("");
      await loadAll();
      await loadDispatch();
    } catch {
      toast.error("Reset failed");
    }
  }

  async function downloadCsv() {
    try {
      const res = await api.get("/exports/customers.csv", { responseType: "blob" });
      const url = window.URL.createObjectURL(new Blob([res.data], { type: "text/csv" }));
      const a = document.createElement("a");
      a.href = url;
      const d = new Date().toISOString().slice(0, 10);
      a.download = `zmex-pnl-${d}.csv`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      window.URL.revokeObjectURL(url);
      toast.success("P&L CSV downloaded");
    } catch (e) {
      toast.error("CSV export failed");
    }
  }

  const noData = data && data.total_customers === 0;

  return (
    <div className="space-y-6" data-testid="admin-dashboard">
      <div className="flex items-end justify-between flex-wrap gap-3">
        <div>
          <div className="text-xs font-bold uppercase tracking-[0.3em] text-[#5C738A]">Overview</div>
          <h1 className="font-heading text-4xl font-black tracking-tight text-[#012340] mt-1">
            Operations Dashboard
          </h1>
          <p className="text-sm text-[#5C738A] mt-2">
            Live snapshot of ZMEX Disposal — real customer, route, and service data.
          </p>
        </div>
        {!isVisitor && (
          <button
            onClick={downloadCsv}
            className="inline-flex items-center gap-2 px-4 py-2 rounded-sm bg-[#CFAB7A] text-[#012340] text-sm font-bold hover:brightness-105"
            data-testid="export-csv-btn"
          >
            <DownloadSimple size={16} weight="bold" /> Export P&amp;L CSV
          </button>
        )}
      </div>

      {/* Inline disposal-cost controls — hidden for visitor since they're editable */}
      {!isVisitor && (
      <div className="bg-[#012340] text-white rounded-sm p-4" data-testid="disposal-controls">
        <div className="flex items-center justify-between flex-wrap gap-3">
          <div className="flex items-center gap-2">
            <div className="w-8 h-8 rounded-sm bg-[#CFAB7A] grid place-items-center text-[#012340]"><Sliders size={16} weight="bold" /></div>
            <div>
              <div className="text-[10px] uppercase tracking-[0.3em] text-[#CFAB7A] font-bold">Disposal Cost Controls</div>
              <div className="text-xs text-white/70">Edits update the dashboard, customers, and routes instantly.</div>
            </div>
          </div>
          <div className="flex flex-wrap gap-3">
            <label className="flex items-center gap-2 bg-white/5 border border-white/10 rounded-sm px-3 py-2">
              <span className="text-[10px] uppercase tracking-[0.2em] text-[#CFAB7A] font-bold">Tipping $/ton</span>
              <span className="text-white/40">$</span>
              <input
                type="number" step="0.01" min="0"
                value={tipping}
                onChange={(e) => onTippingChange(e.target.value)}
                className="w-24 bg-transparent border-0 text-white font-heading font-bold focus:outline-none"
                data-testid="tipping-input"
              />
            </label>
            <label className="flex items-center gap-2 bg-white/5 border border-white/10 rounded-sm px-3 py-2">
              <span className="text-[10px] uppercase tracking-[0.2em] text-[#CFAB7A] font-bold">Lbs / yd³</span>
              <input
                type="number" step="1" min="0"
                value={ppy}
                onChange={(e) => onPpyChange(e.target.value)}
                className="w-20 bg-transparent border-0 text-white font-heading font-bold focus:outline-none"
                data-testid="ppy-input"
              />
            </label>
          </div>
        </div>
      </div>
      )}

      {!isVisitor && <TonsPerYardCard onSaved={loadAll} />}

      {/* Real disposal tickets — sits next to the "estimated" controls above */}
      <ActualDisposalPanel estimatedDisposal={data?.estimated_disposal_cost} readOnly={isVisitor} />

      {!isVisitor && <YardLocationsCard />}

      {/* Empty state */}
      {noData && (
        <div className="bg-white border border-dashed border-[#012340]/30 rounded-sm p-6 text-center" data-testid="empty-state">
          <div className="font-heading text-xl font-bold text-[#012340]">No customers yet</div>
          <p className="text-sm text-[#5C738A] mt-1">Add your first customer to start populating revenue, yardage, and disposal projections.</p>
        </div>
      )}

      {/* Period-aware company analytics (replaces the legacy fixed KPI cards) */}
      <CompanyAnalyticsPanel />

      <DispatchBoard
        dispatch={dispatch}
        previewDay={previewDay}
        setPreviewDay={setPreviewDay}
        loading={loadingDispatch}
        onRefresh={loadDispatch}
      />

      <CompletionHistoryPanel />

      <div className="bg-white border border-[#012340]/10 rounded-sm">
        <div className="px-5 py-4 border-b border-[#012340]/10 flex items-center justify-between">
          <div>
            <div className="text-xs font-bold uppercase tracking-[0.25em] text-[#5C738A]">Active Routes</div>
            <div className="font-heading font-bold text-lg text-[#012340]">By route number</div>
          </div>
          <Truck size={20} weight="bold" className="text-[#012340]" />
        </div>
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead className="text-[11px] uppercase tracking-[0.2em] text-[#5C738A]">
              <tr className="text-left">
                <th className="px-5 py-3">Route #</th>
                <th className="px-5 py-3">Name</th>
                <th className="px-5 py-3">Stops</th>
              </tr>
            </thead>
            <tbody>
              {routes.map((r) => (
                <tr key={r.id} className="even:bg-[#FAFAFA] border-t border-[#012340]/5">
                  <td className="px-5 py-3">
                    <span className="inline-flex items-center px-2 py-0.5 rounded-sm bg-[#CFAB7A]/20 text-[#012340] text-xs font-bold font-heading">
                      #{r.route_number || "—"}
                    </span>
                  </td>
                  <td className="px-5 py-3 font-semibold text-[#012340]">{r.name || "—"}</td>
                  <td className="px-5 py-3">{r.stop_ids?.length || 0}</td>
                </tr>
              ))}
              {!routes.length && (
                <tr><td colSpan={3} className="px-5 py-6 text-center text-[#5C738A]">No routes yet. Create one from the Routes page.</td></tr>
              )}
            </tbody>
          </table>
        </div>
      </div>

      {/* Danger zone — admin only, never rendered for visitor/mentor accounts */}
      {!isVisitor && (
      <div className="border border-[#D32F2F]/30 rounded-sm bg-white" data-testid="danger-zone">
        <div className="px-5 py-4 border-b border-[#D32F2F]/20 flex items-center justify-between">
          <div>
            <div className="text-xs font-bold uppercase tracking-[0.25em] text-[#D32F2F]">Danger Zone</div>
            <div className="text-sm text-[#5C738A] mt-1">Permanently delete all customers, routes, completions, and report entries. Admin account and settings are preserved.</div>
          </div>
          <button onClick={() => setShowReset(true)} className="px-4 py-2 rounded-sm border border-[#D32F2F]/40 text-[#D32F2F] text-sm font-bold hover:bg-[#D32F2F]/5" data-testid="reset-data-btn">
            Reset All Data
          </button>
        </div>
      </div>
      )}

      {showReset && (
        <div className="fixed inset-0 z-40 bg-black/40 grid place-items-center p-4" onClick={() => setShowReset(false)}>
          <div onClick={(e) => e.stopPropagation()} className="bg-white w-full max-w-md rounded-sm border border-[#D32F2F]/30" data-testid="reset-modal">
            <div className="px-5 py-4 border-b border-[#D32F2F]/20">
              <div className="font-heading text-xl font-black text-[#D32F2F]">Reset all operational data?</div>
              <p className="text-sm text-[#5C738A] mt-2">
                This will permanently delete every customer, route, completion record, and report entry. Your admin account, settings, and any drivers you created will remain.
              </p>
              <p className="text-sm text-[#012340] mt-3">Type <span className="font-heading font-black">RESET</span> to confirm.</p>
              <input
                value={resetText}
                onChange={(e) => setResetText(e.target.value)}
                placeholder="RESET"
                className="mt-2 w-full rounded-sm bg-white border border-[#012340]/20 px-3 py-2 text-sm font-heading font-bold focus:outline-none focus:ring-2 focus:ring-[#D32F2F]"
                data-testid="reset-confirm-input"
              />
            </div>
            <div className="px-5 py-3 flex justify-end gap-2">
              <button onClick={() => { setShowReset(false); setResetText(""); }} className="px-4 py-2 rounded-sm border border-[#012340]/20 text-sm font-bold">Cancel</button>
              <button
                onClick={doReset}
                disabled={resetText.trim().toUpperCase() !== "RESET"}
                className="px-4 py-2 rounded-sm bg-[#D32F2F] text-white text-sm font-bold disabled:opacity-40"
                data-testid="reset-confirm-btn"
              >
                Yes, wipe everything
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

function DispatchBoard({ dispatch, previewDay, setPreviewDay, loading, onRefresh }) {
  const totals = dispatch?.totals || { routes: 0, stops: 0, completed: 0, issues: 0, pending: 0, percent: 0 };
  const today = dispatch?.today || "—";
  const isPreview = dispatch?.is_preview;
  const todayReal = dispatch?.today_real;
  const serviceDate = dispatch?.service_date;

  function formatDateLabel(iso) {
    if (!iso) return "";
    try {
      const [y, m, d] = iso.split("-").map(Number);
      const dt = new Date(Date.UTC(y, m - 1, d));
      return dt.toLocaleDateString(undefined, { month: "short", day: "numeric", year: "numeric", timeZone: "UTC" });
    } catch { return iso; }
  }

  return (
    <section className="bg-white border border-[#012340]/10 rounded-sm" data-testid="dispatch-board">
      <header className="px-5 py-4 border-b border-[#012340]/10 flex flex-wrap items-center justify-between gap-3">
        <div className="min-w-0">
          <div className="text-[11px] font-bold uppercase tracking-[0.25em] text-[#5C738A]">Dispatch · Today</div>
          <div className="flex items-baseline gap-2 mt-0.5 flex-wrap">
            <span className="font-heading font-bold text-xl text-[#012340]">{today === todayReal ? "Today" : today}</span>
            {serviceDate && (
              <span className="text-xs text-[#012340]/70 font-mono" data-testid="dispatch-service-date">{formatDateLabel(serviceDate)}</span>
            )}
            <span className="text-xs text-[#5C738A]">· {totals.routes} active routes · {totals.stops} stops scheduled</span>
            {isPreview && (
              <span className="px-2 py-0.5 rounded-sm bg-[#F57C00]/15 text-[#F57C00] text-[10px] font-bold uppercase tracking-[0.2em]">Preview</span>
            )}
          </div>
        </div>
        <div className="flex flex-wrap items-center gap-2">
          <div className="flex items-center gap-1 flex-wrap">
            <span className="text-[10px] uppercase tracking-[0.2em] text-[#5C738A] font-bold mr-1">Day</span>
            <button onClick={() => setPreviewDay("")} data-testid="dispatch-day-today" className={`px-2.5 py-1 rounded-sm text-[11px] font-bold ${!previewDay ? "bg-[#012340] text-white" : "bg-white border border-[#012340]/15 text-[#012340]"}`}>Today</button>
            {DAYS.map((d) => (
              <button
                key={d}
                onClick={() => setPreviewDay(d === todayReal ? "" : d)}
                data-testid={`dispatch-day-${d}`}
                className={`px-2.5 py-1 rounded-sm text-[11px] font-bold ${previewDay === d ? "bg-[#012340] text-white" : "bg-white border border-[#012340]/15 text-[#012340]"}`}
              >
                {d}
              </button>
            ))}
          </div>
          <button onClick={onRefresh} disabled={loading} className="p-2 rounded-sm border border-[#012340]/15 hover:bg-[#012340]/5 disabled:opacity-50" data-testid="dispatch-refresh">
            <ArrowsClockwise size={14} weight="bold" className={loading ? "animate-spin" : ""} />
          </button>
        </div>
      </header>

      <div className="px-5 py-4 border-b border-[#012340]/10 grid grid-cols-2 md:grid-cols-5 gap-4">
        <Mini label="Routes" value={totals.routes} />
        <Mini label="Stops" value={totals.stops} />
        <Mini label="Completed" value={totals.completed} positive />
        <Mini label="Issues" value={totals.issues} warning />
        <div>
          <div className="text-[10px] uppercase tracking-[0.2em] text-[#5C738A] font-bold">Overall Progress</div>
          <div className="font-heading text-2xl font-black text-[#012340] mt-1">{totals.percent}%</div>
          <div className="mt-1.5 h-1.5 bg-[#012340]/10 rounded-full overflow-hidden">
            <div className="h-full bg-[#2E7D32]" style={{ width: `${totals.percent}%` }} />
          </div>
        </div>
      </div>

      <div className="p-5">
        {!dispatch?.routes?.length ? (
          <div className="py-10 text-center text-sm text-[#5C738A]">
            No routes scheduled for {today}.
            {totals.stops === 0 && <div className="text-xs mt-1">Check a different day above to preview, or assign drivers + customer service days first.</div>}
          </div>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-4">
            {dispatch.routes.map((r) => <RouteCard key={r.route_id} r={r} />)}
          </div>
        )}
      </div>
    </section>
  );
}

function RouteCard({ r }) {
  const [open, setOpen] = useState(false);
  return (
    <div className="border border-[#012340]/10 rounded-sm overflow-hidden bg-white" data-testid={`dispatch-route-${r.route_number}`}>
      <button onClick={() => setOpen((s) => !s)} className="w-full text-left px-4 py-3 hover:bg-[#FAFAFA]">
        <div className="flex items-center justify-between gap-2">
          <div className="flex items-center gap-2 min-w-0">
            <span className="font-heading font-black text-[#012340]">#{r.route_number}</span>
            {r.route_name && <span className="text-xs text-[#5C738A] truncate">· {r.route_name}</span>}
          </div>
          <div className="text-[10px] uppercase tracking-[0.2em] text-[#5C738A] font-bold">{r.percent}%</div>
        </div>
        <div className="mt-1 text-xs text-[#5C738A] flex items-center gap-2">
          <Truck size={12} weight="bold" />
          <span>{r.driver_name || "Unassigned"}</span>
          <span>·</span>
          <span>{r.total_stops} stops</span>
        </div>
        <div className="mt-2 h-1.5 bg-[#012340]/10 rounded-full overflow-hidden flex">
          {r.completed > 0 && <div className="h-full bg-[#2E7D32]" style={{ width: `${(r.completed / r.total_stops) * 100}%` }} />}
          {r.issues > 0 && <div className="h-full bg-[#F57C00]" style={{ width: `${(r.issues / r.total_stops) * 100}%` }} />}
        </div>
        <div className="mt-2 flex items-center gap-3 text-[11px]">
          <Stat icon={CheckCircle} color="#2E7D32" label="Done" value={r.completed} />
          <Stat icon={Warning} color="#F57C00" label="Issue" value={r.issues} />
          <Stat icon={Clock} color="#5C738A" label="Pending" value={r.pending} />
        </div>
      </button>
      {open && (
        <ul className="border-t border-[#012340]/10 divide-y divide-[#012340]/5 max-h-72 overflow-y-auto">
          {r.stops.map((s, i) => (
            <li key={s.id} className="px-4 py-2.5 flex items-center gap-3">
              <StopBadge num={i + 1} status={s.status} />
              <div className="flex-1 min-w-0">
                <div className="text-sm font-semibold text-[#012340] truncate">{s.business_name}</div>
                {s.service_address && (
                  <div className="text-xs text-[#5C738A] truncate">{s.service_address}</div>
                )}
                {s.status === "issue" && s.issue_type && (
                  <div className="text-[10px] uppercase tracking-[0.15em] font-bold text-[#F57C00] mt-0.5">⚠ {s.issue_type.replace(/_/g, " ")}</div>
                )}
              </div>
              <span className="text-[10px] text-[#5C738A] font-mono flex-shrink-0">{s.waste_stream || "MSW"} · {s.dumpster_size}yd³</span>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}

function StopBadge({ num, status }) {
  const map = {
    completed: { bg: "#2E7D32", fg: "#fff", label: "✓" },
    issue: { bg: "#F57C00", fg: "#fff", label: "!" },
    pending: { bg: "#012340", fg: "#CFAB7A", label: num },
  };
  const s = map[status] || map.pending;
  return (
    <div className="w-7 h-7 rounded-sm grid place-items-center font-heading font-bold text-sm flex-shrink-0" style={{ background: s.bg, color: s.fg }}>
      {s.label}
    </div>
  );
}

function Stat({ icon: Icon, color, label, value }) {
  return (
    <div className="inline-flex items-center gap-1">
      <Icon size={12} weight="bold" style={{ color }} />
      <span className="text-[#5C738A]">{label}</span>
      <span className="font-bold text-[#012340]">{value}</span>
    </div>
  );
}

function Mini({ label, value, positive, warning }) {
  return (
    <div>
      <div className="text-[10px] uppercase tracking-[0.2em] text-[#5C738A] font-bold">{label}</div>
      <div className={`font-heading text-2xl font-black mt-1 ${positive ? "text-[#2E7D32]" : warning ? "text-[#F57C00]" : "text-[#012340]"}`}>{value}</div>
    </div>
  );
}

function CompletionHistoryPanel() {
  const [items, setItems] = useState([]);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(0);
  const [filter, setFilter] = useState("all"); // all | completed | issue
  const [loading, setLoading] = useState(false);
  const [expandedPhoto, setExpandedPhoto] = useState(null); // { urls, idx }
  const PAGE = 25;

  // Esc closes the photo lightbox; arrow keys flip between photos
  useEffect(() => {
    if (!expandedPhoto) return;
    const n = expandedPhoto.urls.length;
    const onKey = (e) => {
      if (e.key === "Escape") setExpandedPhoto(null);
      if (e.key === "ArrowRight") setExpandedPhoto((p) => ({ ...p, idx: (p.idx + 1) % n }));
      if (e.key === "ArrowLeft") setExpandedPhoto((p) => ({ ...p, idx: (p.idx - 1 + n) % n }));
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [expandedPhoto]);

  async function load() {
    setLoading(true);
    try {
      const params = { limit: PAGE, offset: page * PAGE };
      if (filter !== "all") params.status = filter;
      const { data } = await api.get("/admin/completions", { params });
      // Handle both new paginated shape and legacy raw array
      if (Array.isArray(data)) {
        setItems(data);
        setTotal(data.length);
      } else {
        setItems(data.items || []);
        setTotal(data.total || 0);
      }
    } finally {
      setLoading(false);
    }
  }
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [filter, page]);
  useEffect(() => { setPage(0); }, [filter]);

  function timeLocal(iso) {
    if (!iso) return "—";
    const d = new Date(iso);
    return d.toLocaleString(undefined, { month: "short", day: "numeric", hour: "2-digit", minute: "2-digit" });
  }

  const pages = Math.max(1, Math.ceil(total / PAGE));

  return (
    <section className="bg-white border border-[#012340]/10 rounded-sm" data-testid="completion-history-panel">
      <header className="px-5 py-4 border-b border-[#012340]/10 flex flex-wrap items-center justify-between gap-3">
        <div>
          <div className="text-[11px] font-bold uppercase tracking-[0.25em] text-[#5C738A]">Audit Trail</div>
          <div className="font-heading font-bold text-lg text-[#012340]">Completion History</div>
        </div>
        <div className="flex items-center gap-1">
          {[
            { v: "all", l: "All" },
            { v: "completed", l: "Completed" },
            { v: "issue", l: "Issues" },
          ].map((opt) => (
            <button
              key={opt.v}
              onClick={() => setFilter(opt.v)}
              data-testid={`history-filter-${opt.v}`}
              className={`px-3 py-1.5 rounded-sm text-xs font-bold ${filter === opt.v ? "bg-[#012340] text-white" : "bg-white border border-[#012340]/15 text-[#012340]"}`}
            >
              {opt.l}
            </button>
          ))}
          <button onClick={load} disabled={loading} className="p-2 rounded-sm border border-[#012340]/15 hover:bg-[#012340]/5 disabled:opacity-50">
            <ArrowsClockwise size={14} weight="bold" className={loading ? "animate-spin" : ""} />
          </button>
        </div>
      </header>
      <div className="px-5 py-2 text-[11px] text-[#5C738A] italic border-b border-[#012340]/5" data-testid="photo-retention-note">
        Photos are stored for 30 days after upload and then automatically deleted.
      </div>
      <div className="overflow-auto max-h-[480px]" data-testid="completion-history-scroll">
        <table className="w-full text-sm" data-testid="completion-history-table">
          <thead className="text-[10px] uppercase tracking-[0.2em] text-[#5C738A] bg-white sticky top-0 z-10 shadow-[0_1px_0_0_rgba(1,35,64,0.08)]">
            <tr className="text-left">
              <th className="px-4 py-3">When</th>
              <th className="px-4 py-3">Customer</th>
              <th className="px-4 py-3">Route</th>
              <th className="px-4 py-3">Driver</th>
              <th className="px-4 py-3">Status</th>
              <th className="px-4 py-3">Issue / Note</th>
              <th className="px-4 py-3">GPS</th>
              <th className="px-4 py-3">Photo</th>
            </tr>
          </thead>
          <tbody>
            {items.map((row) => (
              <tr key={row.id} className="border-t border-[#012340]/5 even:bg-[#FAFAFA]" data-testid={`history-row-${row.id}`}>
                <td className="px-4 py-3 text-xs text-[#012340] font-mono whitespace-nowrap">{timeLocal(row.completed_at)}</td>
                <td className="px-4 py-3">
                  <div className="font-semibold text-[#012340] text-sm truncate max-w-[200px]">{row.customer_name || "—"}</div>
                  {row.customer_address && (
                    <div className="text-[11px] text-[#5C738A] truncate max-w-[200px]">{row.customer_address}</div>
                  )}
                </td>
                <td className="px-4 py-3">
                  <span className="inline-flex items-center px-2 py-0.5 rounded-sm bg-[#CFAB7A]/20 text-[#012340] text-xs font-bold font-heading">
                    #{row.route_number || "—"}
                  </span>
                </td>
                <td className="px-4 py-3 text-xs text-[#012340]">{row.driver_name || "—"}</td>
                <td className="px-4 py-3">
                  {row.status === "completed" ? (
                    <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-sm bg-[#2E7D32]/10 text-[#2E7D32] text-xs font-bold">
                      <CheckCircle size={12} weight="bold" /> Completed
                    </span>
                  ) : (
                    <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-sm bg-[#F57C00]/10 text-[#F57C00] text-xs font-bold">
                      <Warning size={12} weight="bold" /> Issue
                    </span>
                  )}
                </td>
                <td className="px-4 py-3 text-xs text-[#5C738A]">
                  {row.status === "issue" ? (
                    <>
                      <div className="font-bold text-[#012340]">{(row.issue_type || "").replace(/_/g, " ")}</div>
                      {row.notes && <div className="italic">{row.notes}</div>}
                    </>
                  ) : (row.notes || "—")}
                </td>
                <td className="px-4 py-3 text-[10px] text-[#5C738A] font-mono whitespace-nowrap">
                  {row.gps_lat != null && row.gps_lng != null ? (
                    <a href={`https://www.google.com/maps?q=${row.gps_lat},${row.gps_lng}`} target="_blank" rel="noreferrer" className="hover:text-[#012340]">
                      📍 {row.gps_lat.toFixed(4)}, {row.gps_lng.toFixed(4)}
                    </a>
                  ) : "—"}
                </td>
                <td className="px-4 py-3">
                  {row.photo_url ? (
                    <div className="flex flex-col gap-1" data-testid={`history-photo-cell-${row.id}`}>
                      <PhotoThumbStrip
                        rowId={row.id}
                        urls={(row.photo_urls && row.photo_urls.length ? row.photo_urls : [row.photo_url]).map(absPhotoUrl)}
                        onOpen={(urls, idx) => setExpandedPhoto({ urls, idx })}
                      />
                      <div className="flex items-center gap-2">
                        <button onClick={() => setExpandedPhoto({ urls: (row.photo_urls && row.photo_urls.length ? row.photo_urls : [row.photo_url]).map(absPhotoUrl), idx: 0 })} className="text-xs text-[#012340] underline font-bold" data-testid={`history-photo-${row.id}`}>
                          View
                        </button>
                        <a
                          href={`${api.defaults.baseURL}/admin/issues/${row.id}/photos.zip`}
                          onClick={async (e) => {
                            e.preventDefault();
                            try {
                              const resp = await api.get(`/admin/issues/${row.id}/photos.zip`, { responseType: "blob" });
                              const url = window.URL.createObjectURL(new Blob([resp.data], { type: "application/zip" }));
                              const a = document.createElement("a");
                              a.href = url;
                              a.download = `service-issue-${row.id}-photos.zip`;
                              document.body.appendChild(a); a.click(); a.remove();
                              window.URL.revokeObjectURL(url);
                            } catch (err) {
                              alert(err?.response?.status === 410 ? "Photos have expired." : "Download failed.");
                            }
                          }}
                          className="text-xs bg-[#CFAB7A] text-[#012340] font-bold rounded-sm px-2 py-0.5 hover:brightness-105 inline-flex items-center gap-1"
                          data-testid={`history-photo-download-${row.id}`}
                        >
                          ⬇ Download Photos
                        </a>
                      </div>
                      {row.photo_expires_at ? (
                        <div className="text-[10px] text-[#5C738A]">
                          Available until: {new Date(row.photo_expires_at).toLocaleDateString()}
                        </div>
                      ) : null}
                    </div>
                  ) : row.photo_expired || row.has_photo_history ? (
                    <span className="text-[11px] italic text-[#B23A48]" data-testid={`history-photo-expired-${row.id}`}>Photos expired</span>
                  ) : "—"}
                </td>
              </tr>
            ))}
            {!items.length && !loading && (
              <tr><td colSpan={8} className="px-4 py-8 text-center text-xs text-[#5C738A]">No completion records yet.</td></tr>
            )}
          </tbody>
        </table>
      </div>
      {total > PAGE && (
        <div className="flex items-center justify-between px-5 py-3 border-t border-[#012340]/10 text-xs">
          <div className="text-[#5C738A]">
            {page * PAGE + 1}–{Math.min((page + 1) * PAGE, total)} of {total}
          </div>
          <div className="flex items-center gap-1">
            <button onClick={() => setPage((p) => Math.max(0, p - 1))} disabled={page === 0} className="px-3 py-1 rounded-sm border border-[#012340]/15 disabled:opacity-40" data-testid="history-prev">← Prev</button>
            <span className="px-2 font-mono">{page + 1} / {pages}</span>
            <button onClick={() => setPage((p) => Math.min(pages - 1, p + 1))} disabled={page + 1 >= pages} className="px-3 py-1 rounded-sm border border-[#012340]/15 disabled:opacity-40" data-testid="history-next">Next →</button>
          </div>
        </div>
      )}
      {expandedPhoto && (
        <PhotoLightbox
          urls={expandedPhoto.urls}
          idx={expandedPhoto.idx}
          onIdx={(idx) => setExpandedPhoto((p) => ({ ...p, idx }))}
          onClose={() => setExpandedPhoto(null)}
        />
      )}
    </section>
  );
}

const absPhotoUrl = (u) => (u.startsWith("http") ? u : `${api.defaults.baseURL?.replace(/\/api$/, "")}${u}`);

function PhotoThumbStrip({ rowId, urls, onOpen }) {
  const shown = urls.slice(0, 3);
  return (
    <div className="flex items-center gap-1" data-testid={`history-photo-thumbs-${rowId}`}>
      {shown.map((u, i) => (
        <button key={u} onClick={() => onOpen(urls, i)} className="relative w-10 h-10 rounded-sm overflow-hidden border border-[#012340]/15 hover:ring-2 hover:ring-[#CFAB7A]" data-testid={`history-photo-thumb-${rowId}-${i}`}>
          <img src={u} alt={`Issue photo ${i + 1}`} className="w-full h-full object-cover" loading="lazy" />
          {i === 2 && urls.length > 3 && (
            <span className="absolute inset-0 bg-black/60 text-white text-[11px] font-bold grid place-items-center">+{urls.length - 3}</span>
          )}
        </button>
      ))}
    </div>
  );
}

function PhotoLightbox({ urls, idx, onIdx, onClose }) {
  const n = urls.length;
  const btn = "absolute top-1/2 -translate-y-1/2 w-12 h-12 grid place-items-center rounded-full bg-white/15 text-white hover:bg-white/30";
  return (
    <div className="fixed inset-0 z-50 bg-black/90 grid place-items-center p-6" onClick={onClose} data-testid="completion-photo-lightbox">
      <button onClick={onClose} className="absolute top-4 right-4 w-10 h-10 grid place-items-center rounded-full bg-white/15 text-white hover:bg-white/30" aria-label="Close" data-testid="lightbox-close">
        <X size={20} weight="bold" />
      </button>
      {n > 1 && (
        <div className="absolute top-5 left-1/2 -translate-x-1/2 text-white/80 text-xs font-bold tracking-[0.2em]" data-testid="lightbox-counter">{idx + 1} / {n}</div>
      )}
      <PhotoLightboxImage key={urls[idx]} src={urls[idx]} />
      {n > 1 && (
        <>
          <button onClick={(e) => { e.stopPropagation(); onIdx((idx - 1 + n) % n); }} className={`${btn} left-4`} aria-label="Previous photo" data-testid="lightbox-prev">
            <CaretLeft size={22} weight="bold" />
          </button>
          <button onClick={(e) => { e.stopPropagation(); onIdx((idx + 1) % n); }} className={`${btn} right-4`} aria-label="Next photo" data-testid="lightbox-next">
            <CaretRight size={22} weight="bold" />
          </button>
        </>
      )}
    </div>
  );
}

function PhotoLightboxImage({ src }) {
  const [failed, setFailed] = useState(false);
  if (failed) {
    return (
      <div className="bg-white text-[#012340] rounded-sm px-6 py-8 text-center max-w-md" data-testid="completion-photo-failed">
        <Warning size={28} weight="bold" className="text-[#F57C00] mx-auto mb-2" />
        <div className="font-heading font-bold">Image failed to load</div>
        <div className="text-xs text-[#5C738A] mt-1 break-all">{src}</div>
      </div>
    );
  }
  return (
    <img
      src={src}
      alt="Issue evidence"
      onError={() => setFailed(true)}
      onClick={(e) => e.stopPropagation()}
      className="max-h-[90vh] max-w-[90vw] rounded-sm border border-white/20 cursor-default"
      data-testid="completion-photo-img"
    />
  );
}

function TonsPerYardCard({ onSaved }) {
  const STREAMS = [
    { key: "MSW", label: "MSW", defaultVal: 0.05 },
    { key: "Single Stream Recycling", label: "Single Stream Recycling", defaultVal: 0.04 },
    { key: "Cardboard", label: "Cardboard", defaultVal: 0.03 },
    { key: "Yard Waste", label: "Yard Waste", defaultVal: 0.10 },
    { key: "C&D", label: "C&D", defaultVal: 0.20 },
    { key: "Other", label: "Other", defaultVal: 0.05 },
  ];
  const [open, setOpen] = useState(false);
  const [vals, setVals] = useState({});
  const [saving, setSaving] = useState(false);

  async function load() {
    try {
      const { data } = await api.get("/settings");
      const overrides = data.tons_per_yard_by_stream || {};
      const next = {};
      for (const s of STREAMS) {
        next[s.key] = overrides[s.key] != null && overrides[s.key] !== "" ? String(overrides[s.key]) : String(s.defaultVal);
      }
      setVals(next);
    } catch (err) {
      console.warn("Failed to load tons/yard settings", err);
    }
  }
  useEffect(() => { load(); }, []);

  async function save() {
    setSaving(true);
    try {
      const { data: current } = await api.get("/settings");
      const tons_by = {};
      for (const s of STREAMS) {
        const v = Number(vals[s.key]);
        if (!isNaN(v) && v > 0) tons_by[s.key] = v;
      }
      const payload = { ...current, tons_per_yard_by_stream: tons_by };
      delete payload.id;
      delete payload.demo_cleaned;
      delete payload.demo_cleaned_at;
      delete payload.demo_cleaned_summary;
      delete payload.prod_defaults_applied;
      await api.put("/settings", payload);
      toast.success("Tonnage assumptions saved");
      onSaved?.();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Failed to save tonnage settings");
    } finally {
      setSaving(false);
    }
  }

  return (
    <div className="bg-white border border-[#012340]/10 rounded-sm" data-testid="tons-per-yard-card">
      <button
        onClick={() => setOpen((o) => !o)}
        className="w-full flex items-center justify-between px-5 py-4"
        data-testid="tons-per-yard-toggle"
      >
        <div className="text-left">
          <div className="text-[11px] font-bold uppercase tracking-[0.3em] text-[#5C738A]">Tonnage Assumptions</div>
          <div className="font-heading font-bold text-[#012340]">Estimated tons / yd³ per waste stream</div>
        </div>
        <span className="text-xs font-bold text-[#012340]">{open ? "Hide" : "Edit"}</span>
      </button>
      {open && (
        <div className="border-t border-[#012340]/10 px-5 py-4 space-y-3">
          <div className="text-xs text-[#5C738A]">
            These drive <span className="font-bold text-[#012340]">Est. Tonnage</span> and
            therefore <span className="font-bold text-[#012340]">Disposal Cost</span> on the route profile.
            Default values match industry compaction averages.
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3">
            {STREAMS.map((s) => (
              <label key={s.key} className="flex items-center justify-between gap-2 bg-[#FAFAFA] border border-[#012340]/10 rounded-sm px-3 py-2">
                <span className="text-[11px] font-bold uppercase tracking-[0.2em] text-[#5C738A]">{s.label}</span>
                <input
                  type="number"
                  step="0.001"
                  min="0"
                  value={vals[s.key] ?? ""}
                  onChange={(e) => setVals((v) => ({ ...v, [s.key]: e.target.value }))}
                  className="w-24 text-right bg-white border border-[#012340]/20 rounded-sm px-2 py-1 font-mono text-sm focus:outline-none focus:ring-2 focus:ring-[#CFAB7A]"
                  data-testid={`tpy-${s.key.replace(/\s+/g, "-")}`}
                />
                <span className="text-[10px] text-[#5C738A]">tons/yd³</span>
              </label>
            ))}
          </div>
          <div className="flex items-center justify-end gap-2">
            <button onClick={load} className="text-xs text-[#5C738A] hover:text-[#012340]" data-testid="tpy-reset">Reload</button>
            <button
              onClick={save}
              disabled={saving}
              className="inline-flex items-center gap-2 px-4 py-2 rounded-sm bg-[#012340] text-white text-sm font-bold disabled:opacity-50"
              data-testid="tpy-save"
            >
              {saving ? "Saving…" : "Save assumptions"}
            </button>
          </div>
        </div>
      )}
    </div>
  );
}

function YardLocationsCard() {
  const [settings, setSettings] = useState(null);
  const [editing, setEditing] = useState(false);
  const [yardName, setYardName] = useState("");
  const [yardAddress, setYardAddress] = useState("");
  const [yardCoords, setYardCoords] = useState({ lat: null, lng: null });
  const [saving, setSaving] = useState(false);

  async function load() {
    const { data } = await api.get("/settings");
    setSettings(data);
    setYardName(data.yard_name || "ZMEX Yard");
    setYardAddress(data.yard_address || "");
    setYardCoords({ lat: data.yard_lat ?? null, lng: data.yard_lng ?? null });
  }

  useEffect(() => { load(); }, []);

  async function save() {
    setSaving(true);
    try {
      const payload = { ...(settings || {}) };
      delete payload.id;
      delete payload.demo_cleaned;
      delete payload.demo_cleaned_at;
      delete payload.demo_cleaned_summary;
      delete payload.prod_defaults_applied;
      payload.yard_name = yardName.trim() || "ZMEX Yard";
      payload.yard_address = yardAddress.trim();
      if (yardCoords.lat != null && yardCoords.lng != null) {
        payload.yard_lat = yardCoords.lat;
        payload.yard_lng = yardCoords.lng;
      } else if (payload.yard_address !== (settings?.yard_address || "")) {
        const hit = await browserGeocode(payload.yard_address);
        payload.yard_lat = hit?.lat ?? null; payload.yard_lng = hit?.lng ?? null;
      }
      // Mirror to end_* so routes automatically start AND end at the same yard
      payload.end_name = payload.yard_name;
      payload.end_address = "";   // server mirrors yard → end when blank
      payload.end_lat = null;
      payload.end_lng = null;
      const { data } = await api.put("/settings", payload);
      setSettings(data);
      toast.success("Yard saved");
      setEditing(false);
      load();
    } catch {
      toast.error("Failed to save yard");
    } finally {
      setSaving(false);
    }
  }

  const coordsText = settings?.yard_lat && settings?.yard_lng
    ? `${settings.yard_lat.toFixed(5)}, ${settings.yard_lng.toFixed(5)}`
    : "—";

  return (
    <div className="bg-white border border-[#012340]/10 rounded-sm" data-testid="yard-locations-card">
      <div className="px-4 py-3 flex items-center gap-3 flex-wrap">
        <div className="w-8 h-8 rounded-sm bg-[#012340] text-[#CFAB7A] grid place-items-center flex-shrink-0">
          <MapPin size={14} weight="bold" />
        </div>
        <div className="min-w-0 flex-1">
          <div className="text-[10px] uppercase tracking-[0.3em] text-[#5C738A] font-bold">Primary Yard</div>
          {!editing ? (
            <div className="flex items-baseline gap-2 flex-wrap">
              <span className="font-heading font-bold text-[#012340] text-base truncate">{settings?.yard_name || "ZMEX Yard"}</span>
              <span className="text-xs text-[#5C738A] truncate">·  {settings?.yard_address || "No address set"}</span>
              <span className="text-[10px] font-mono text-[#5C738A]">📍 {coordsText}</span>
            </div>
          ) : (
            <div className="font-heading font-bold text-[#012340] text-base">Edit yard</div>
          )}
        </div>
        {!editing ? (
          <button
            onClick={() => setEditing(true)}
            className="px-3 py-1.5 rounded-sm bg-white border border-[#012340]/15 text-[#012340] text-xs font-bold hover:bg-[#012340]/5"
            data-testid="yard-edit-btn"
          >
            Edit
          </button>
        ) : (
          <button
            onClick={() => { setEditing(false); load(); }}
            className="px-3 py-1.5 rounded-sm text-[#5C738A] text-xs font-bold hover:text-[#012340]"
            data-testid="yard-cancel-btn"
          >
            Cancel
          </button>
        )}
      </div>

      {editing && (
        <div className="border-t border-[#012340]/10 px-4 py-3 grid grid-cols-1 md:grid-cols-12 gap-3 items-end">
          <div className="md:col-span-4">
            <label className="text-[10px] font-bold uppercase tracking-[0.2em] text-[#5C738A]">Yard Name</label>
            <input
              value={yardName}
              onChange={(e) => setYardName(e.target.value)}
              placeholder="ZMEX Yard"
              className="mt-1 w-full rounded-sm bg-white border border-[#012340]/20 px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-[#CFAB7A]"
              data-testid="yard-name-input"
            />
          </div>
          <div className="md:col-span-6">
            <label className="text-[10px] font-bold uppercase tracking-[0.2em] text-[#5C738A]">Yard Address</label>
            <AddressAutocomplete
              value={yardAddress}
              onChange={(v) => { setYardAddress(v); setYardCoords({ lat: null, lng: null }); }}
              onPlace={(p) => {
                if (p.__partial) {
                  // pin moved / verified — only update coords
                  setYardCoords({ lat: p.lat, lng: p.lng });
                  return;
                }
                setYardAddress(p.address);
                setYardCoords({ lat: p.lat, lng: p.lng });
              }}
              lat={yardCoords.lat}
              lng={yardCoords.lng}
              placeholder="Enter primary yard address"
              className="mt-1 w-full rounded-sm bg-white border border-[#012340]/20 px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-[#CFAB7A]"
              testid="yard-address-input"
            />
          </div>
          <div className="md:col-span-2 flex md:justify-end">
            <button
              onClick={save}
              disabled={saving || !yardAddress.trim()}
              className="w-full md:w-auto inline-flex items-center justify-center gap-2 px-4 py-2 rounded-sm bg-[#012340] text-white text-sm font-bold disabled:opacity-50"
              data-testid="yard-save-btn"
            >
              <FloppyDisk size={14} weight="bold" /> {saving ? "Saving…" : "Save"}
            </button>
          </div>
          <p className="md:col-span-12 text-[10px] text-[#5C738A]">
            Drivers start and end every route at this single yard. Geocoded via Google Maps on save.
          </p>
        </div>
      )}
    </div>
  );
}
